import { ArrowRight, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "../../lib/LangContext";
import { homeCopy, GOOGLE_PROFILE_URL } from "../../lib/homeCopy";
import { useLocalePath } from "../site/LangRouting";
import { SETS, Slider } from "../site/BeforeAfter";
import { openClubPopup } from "../site/ClubPopup";
import { ChapterLabel, Reveal, StoryImage } from "./Story";
import { GoogleReviewsSection } from "./GoogleReviews";
import { p22Copy } from "../../lib/p22Copy";
import { clubCopy } from "../../lib/clubCopy";

const stripEmoji = (s = "") => s.replace(/^[^\p{L}\p{N}-]+/u, "").trim();

const Heading = ({ index, label, title, text, testid }) => (
  <div className="max-w-2xl">
    <Reveal><ChapterLabel index={index} label={label} testid={testid} /></Reveal>
    <Reveal delay={0.08} as="h2" className="mt-8 font-heading text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">{title}</Reveal>
    {text && <Reveal delay={0.16} as="p" className="mt-6 text-zinc-300 text-[15px] md:text-[17px] leading-relaxed">{text}</Reveal>}
  </div>
);

const LinkCta = ({ to, href, onClick, children, testid }) => {
  const cls = "group inline-flex items-center gap-3 border border-white/20 text-white px-7 py-3.5 text-[12px] tracking-[0.25em] uppercase hover:border-gold hover:text-gold transition-colors duration-300";
  if (to) return <Link to={to} data-testid={testid} className={cls}>{children}<ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" /></Link>;
  if (href) return <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid} className={cls}>{children}<ExternalLink size={13} /></a>;
  return <button type="button" onClick={onClick} data-testid={testid} className={cls}>{children}<ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" /></button>;
};

// 06 — real customer before/after photos only.
export const PortfolioChapter = () => {
  const { lang, t } = useLang();
  const copy = homeCopy(lang);
  const c = copy.portfolio;
  const lp = useLocalePath();
  return (
    <section id="before-after" data-testid="chapter-portfolio" data-chapter="6" className="bg-[#0A0A0C] py-20 md:py-28 border-t border-white/[0.06]">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <Heading index={6} label={copy.chapters[5]} title={c.title} text={c.text} testid="chapter-portfolio-label" />
          <Reveal><LinkCta to={lp("/arbeiten")} testid="portfolio-cta">{c.cta}</LinkCta></Reveal>
        </div>
        <div className="mt-12 md:mt-14 grid lg:grid-cols-2 gap-6">
          {["cars", "sofas"].map((k, i) => (
            <Reveal key={k} delay={0.1 * i}>
              <div className="flex items-center justify-between mb-4 text-[11px] tracking-[0.25em] uppercase">
                <span className="text-zinc-300">{t.beforeAfter.categories[k]}</span>
                <span data-testid={`portfolio-real-tag-${k}`} className="text-gold/80">{c.real}</span>
              </div>
              <Slider before={SETS[k].before} after={SETS[k].after} beforeLabel={t.beforeAfter.beforeLabel} afterLabel={t.beforeAfter.afterLabel} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

// 07 — real Google reviews when the Places key is configured; otherwise a clean link-only fallback.
export const TrustChapter = () => {
  const { lang } = useLang();
  return <GoogleReviewsSection lang={lang} />;
};

// 08 — Club teaser using the existing approved Club texts; opens the existing sign-up popup.
export const ClubChapter = () => {
  const { lang } = useLang();
  const copy = homeCopy(lang);
  const k = clubCopy(lang);
  return (
    <section id="club" data-testid="chapter-club" data-chapter="8" className="relative bg-[#0A0A0C] py-16 md:py-24 border-t border-white/[0.06] overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-6">
          <ChapterLabel index={8} label={copy.chapters[7]} testid="chapter-club-label" />
          <Reveal delay={0.05} className="mt-6 text-[12px] tracking-[0.35em] uppercase text-zinc-400">PUREGLOSS CLUB</Reveal>
          <Reveal delay={0.1} className="mt-4 flex items-end gap-5">
            <span className="font-heading text-white text-6xl sm:text-7xl lg:text-8xl leading-[0.9] tracking-tight">{k.amount}</span>
            <span className="pb-2 text-[12px] md:text-[13px] tracking-[0.28em] uppercase text-gold/90">{k.badge}</span>
          </Reveal>
          <Reveal delay={0.15} as="p" className="mt-6 text-zinc-300 text-[16px] md:text-[18px] leading-relaxed max-w-lg">{k.offer}</Reveal>
          <Reveal delay={0.2} className="mt-9">
            <div className="text-[11px] tracking-[0.3em] uppercase text-zinc-500">{k.benefitsTitle}</div>
            <ol className="mt-4 grid sm:grid-cols-3 gap-px bg-white/[0.06] rounded-2xl overflow-hidden">
              {k.benefits.map((b, i) => (
                <li key={b} data-testid={`club-teaser-benefit-${i}`} className="bg-[#0d0d10] p-5">
                  <span className="font-mono text-[11px] text-gold/80">0{i + 1}</span>
                  <p className="mt-3 text-[14px] text-zinc-200 leading-relaxed">{b}</p>
                </li>
              ))}
            </ol>
          </Reveal>
          <Reveal delay={0.25} className="mt-9 flex flex-col sm:flex-row sm:items-center gap-5">
            <button type="button" onClick={openClubPopup} data-testid="club-teaser-cta"
              className="group inline-flex items-center justify-center gap-3 rounded-full bg-white text-black px-8 py-4 text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-gold transition-colors duration-300">
              {k.cta} <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <span className="text-[12px] text-zinc-500 leading-relaxed max-w-xs">{k.finePrint}</span>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="lg:col-span-6 relative group">
          <StoryImage name="transformation-wide" alt="" tag={copy.services.film} sizes="(min-width: 1024px) 45vw, 100vw" className="aspect-[4/3] rounded-[20px] overflow-hidden" />
        </Reveal>
      </div>
    </section>
  );
};

// 09 — Academy teaser linking to the existing Academy page.
export const AcademyChapter = () => {
  const { lang, t } = useLang();
  const copy = homeCopy(lang);
  const a = t.ctaBlocks.academy;
  const lp = useLocalePath();
  return (
    <section id="academy-teaser" data-testid="chapter-academy" data-chapter="9" className="bg-[#050505] py-20 md:py-28 border-t border-white/[0.06]">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-6 lg:order-1 order-2 relative group">
          <StoryImage name="concept-academy-tools" widths={[640, 1264]} alt="" tag={p22Copy(lang).academyConcept} testid="academy-teaser-image" sizes="(min-width: 1024px) 45vw, 100vw" className="aspect-[4/3]" />
        </Reveal>
        <div className="lg:col-span-6 lg:order-2 order-1">
          <Heading index={9} label={copy.chapters[8]} title={a.title} text={a.desc} testid="chapter-academy-label" />
          <Reveal delay={0.25} className="mt-10"><LinkCta to={lp("/academy")} testid="academy-teaser-cta">{copy.academy.cta}</LinkCta></Reveal>
        </div>
      </div>
    </section>
  );
};
